import { Component, inject, Injector, output, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaButtonComponent, RaInputComponent, RaToastService } from '@shared/components';
import { DocumentContractController } from 'src/app/domain/controllers/document/document-contract.controller';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { UtilService } from 'src/app/shared/utils/utils.service';

@Component({
    selector: 'signature-flow',
    templateUrl: 'signature-flow.component.html',
    styleUrl: 'signature-flow.component.scss',
    imports: [RaSquareIconComponent, RaInputComponent, RaButtonComponent, TranslatePipe, RaIconComponent],
})
export class SignatureFlowComponent {
    public close = output<void>();
    public continue = output<void>();
    public name = new FormControl('', [Validators.required, Validators.maxLength(80)]);
    public readonly signatureOptions: { label: string; fontFamily: string }[] = [
        { label: 'Mrs Saint Delafield', fontFamily: "'Mrs Saint Delafield', cursive" },
        { label: 'Cedarville Cursive', fontFamily: "'Cedarville Cursive', cursive" },
        { label: 'Architects Daughter', fontFamily: "'Architects Daughter', cursive" },
        { label: 'Sacramento', fontFamily: "'Sacramento', cursive" },
    ];
    public selectedFontFamily = this.signatureOptions[0].fontFamily;
    public signLoading = signal(false);
    public showConfirmation = signal(false);

    // * [Injects]
    private _utilService = inject(UtilService);
    private _activateRoute = inject(ActivatedRoute);
    private _raToastService = inject(RaToastService);
    private _documentContractController = inject(DocumentContractController);
    private _injector = inject(Injector);

    public selectSignature(fontFamily: string): void {
        this.selectedFontFamily = fontFamily;
    }

    public signaturePreview(): string {
        const value = this.name.value?.trim();
        return value ? value : this._injector.get(TranslateService).instant('document.edit.signaturePreviewPlaceholder');
    }

    public handleContinue(): void {
        if (!this._utilService.isControlValid(this.name)) {
            this._raToastService.warning({
                title: this._injector.get(TranslateService).instant('common.warning'),
                message: this._injector.get(TranslateService).instant('common.fieldInvalid'),
            });

            return;
        }

        this.showConfirmation.set(true);
    }

    public closeConfirmation(): void {
        if (this.signLoading()) return;
        this.showConfirmation.set(false);
    }

    public confirmSignature(): void {
        this.signLoading.set(true);

        const contractId = this._activateRoute.snapshot.params['id'];
        this._documentContractController
            .signContract(contractId, this.name.value, this.selectedFontFamily)
            .subscribe({
                next: () => {
                    this.showConfirmation.set(false);
                    this.signLoading.set(false);
                    this.continue.emit();
                },
                error: () => {
                    this.signLoading.set(false);
                },
            });
    }
}
